import { FC, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from "../hooks/use-firebase"
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth"
import { Button, Input } from "../components/ui"
import { Eye, EyeOff, CheckCircle, XCircle, Loader2 } from "lucide-react"
import { toast } from "sonner"

type ActionMode = "resetPassword" | "verifyEmail" | "recoverEmail" | null

type ActionState = "loading" | "input" | "success" | "error"

export const AuthAction: FC = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const mode = searchParams.get("mode") as ActionMode
  const oobCode = searchParams.get("oobCode") || ""

  const [state, setState] = useState<ActionState>("loading")
  const [errorMessage, setErrorMessage] = useState("")
  const [email, setEmail] = useState("")

  // Password reset state
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!mode || !oobCode) {
      setErrorMessage("Invalid action link. Please request a new one.")
      setState("error")
      return
    }

    handleAction()
  }, [mode, oobCode])

  const handleAction = async () => {
    try {
      switch (mode) {
        case "verifyEmail":
          await handleVerifyEmail()
          break
        case "resetPassword":
          await handleVerifyResetCode()
          break
        case "recoverEmail":
          await handleRecoverEmail()
          break
        default:
          setErrorMessage("Unknown action type.")
          setState("error")
      }
    } catch (error: any) {
      console.error("Auth action error:", error)
      setErrorMessage(getErrorMessage(error.code))
      setState("error")
    }
  }

  const handleVerifyEmail = async () => {
    await applyActionCode(auth, oobCode)
    setState("success")
  }

  const handleVerifyResetCode = async () => {
    // Verify the code is valid and get the email
    const userEmail = await verifyPasswordResetCode(auth, oobCode)
    setEmail(userEmail)
    setState("input")
  }

  const handleRecoverEmail = async () => {
    await applyActionCode(auth, oobCode)
    setState("success")
  }

  const handlePasswordReset = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields")
      return
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters")
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setIsSubmitting(true)

    try {
      await confirmPasswordReset(auth, oobCode, newPassword)
      setState("success")
      toast.success("Password reset successfully!")
    } catch (error: any) {
      console.error("Password reset error:", error)
      toast.error(getErrorMessage(error.code))
    } finally {
      setIsSubmitting(false)
    }
  }

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case "auth/expired-action-code":
        return "This link has expired. Please request a new one."
      case "auth/invalid-action-code":
        return "This link is invalid or has already been used. Please request a new one."
      case "auth/user-disabled":
        return "This account has been disabled."
      case "auth/user-not-found":
        return "No account found for this email."
      case "auth/weak-password":
        return "Password is too weak. Please use a stronger password."
      default:
        return "An error occurred. Please try again."
    }
  }

  const getTitle = (): string => {
    switch (mode) {
      case "verifyEmail":
        return state === "success" ? "Email Verified!" : "Verifying Email..."
      case "resetPassword":
        return state === "success" ? "Password Reset!" : "Reset Your Password"
      case "recoverEmail":
        return state === "success" ? "Email Recovered!" : "Recovering Email..."
      default:
        return "Processing..."
    }
  }

  const getSuccessMessage = (): string => {
    switch (mode) {
      case "verifyEmail":
        return "Your email has been verified successfully. You can now access all features."
      case "resetPassword":
        return "Your password has been reset successfully. You can now log in with your new password."
      case "recoverEmail":
        return "Your email has been recovered successfully."
      default:
        return "Action completed successfully."
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting) {
      handlePasswordReset()
    }
  }

  return (
    <div className="w-screen h-screen flex flex-col md:flex-row">
      <div className="w-none md:w-1/2 flex justify-center items-center">
        <div
          className="font-extrabold text-[30px] text-center text-black dark:text-white md:text-[60px] cursor-pointer"
          onClick={() => navigate("/")}
        >
          INVENTORY<span className="text-sumoPrimary">SUMO</span>
        </div>
      </div>

      <div className="w-full h-full md:w-1/2 bg-black flex justify-center items-center md:items-center">
        <div className="w-[392px] flex flex-col gap-4 p-6 bg-black text-white rounded-sm shadow">
          <div className="text-white text-2xl">{getTitle()}</div>

          {/* Loading State */}
          {state === "loading" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-12 h-12 animate-spin text-sumoPrimary" />
              <p className="text-neutral400">Please wait...</p>
            </div>
          )}

          {/* Error State */}
          {state === "error" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <XCircle className="w-16 h-16 text-error500" />
              <p className="text-center text-neutral300">{errorMessage}</p>
              <div className="flex flex-col gap-2 w-full mt-4">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate("/login")}
                >
                  Go to Login
                </Button>
                {mode === "resetPassword" && (
                  <Button
                    variant="ghost"
                    className="w-full text-white hover:text-sumoPrimary"
                    onClick={() => navigate("/login")}
                  >
                    Request New Reset Link
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Success State */}
          {state === "success" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle className="w-16 h-16 text-success500" />
              <p className="text-center text-neutral300">{getSuccessMessage()}</p>
              <Button
                variant="primary"
                className="w-full mt-4"
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </div>
          )}

          {/* Password Reset Input State */}
          {state === "input" && mode === "resetPassword" && (
            <div className="flex flex-col gap-4">
              <p className="text-neutral400 text-sm">
                Enter a new password for <span className="text-white">{email}</span>
              </p>

              <div className="text-white flex items-center w-full">
                <Input
                  name="newPassword"
                  className="bg-white text-black h-12"
                  placeholder="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSubmitting}
                />
                <div
                  className="-ml-8 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Eye width={18} /> : <EyeOff width={18} />}
                </div>
              </div>

              <div className="text-white flex items-center w-full">
                <Input
                  name="confirmPassword"
                  className="bg-white text-black h-12"
                  placeholder="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSubmitting}
                />
                <div
                  className="-ml-8 cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <Eye width={18} /> : <EyeOff width={18} />}
                </div>
              </div>

              <Button
                variant="primary"
                className="w-full"
                disabled={!newPassword || !confirmPassword || isSubmitting}
                onClick={handlePasswordReset}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>

              <Button
                variant="ghost"
                className="w-full text-white hover:text-sumoPrimary"
                onClick={() => navigate("/login")}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
